(() => {
  if (customElements.get('ip-events-calendar')) return;
  const parseDate = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.toISOString().slice(0, 10) === value ? date : null;
  };
  const key = date => date.toISOString().slice(0, 10);
  const safeUrl = (value, base = window.location.origin) => {
    if (!value || !value.trim()) return null;
    try {
      const url = new URL(value, base);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
      return url;
    } catch {return null;}
  };
  const customColour = value => {
    let hex = value || '#28625e';
    if (/^#[0-9a-f]{3}$/i.test(hex)) hex = '#' + [...hex.slice(1)].map(char => char + char).join('');
    return /^#[0-9a-f]{6}$/i.test(hex) ? hex : '#28625e';
  };
  const colourInk = hex => {
    const rgb = [1,3,5].map(index => parseInt(hex.slice(index,index+2),16)/255);
    const linear = rgb.map(c => c<=.04045 ? c/12.92 : ((c+.055)/1.055)**2.4);
    return linear[0]*.2126+linear[1]*.7152+linear[2]*.0722 > .179 ? '#111111' : '#ffffff';
  };
  const create = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  };
  class IPEventsCalendar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.year = 2027;
      this.month = 0;
      this.view = 'year';
      this.category = 'all';
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.locale = document.documentElement.lang || 'en-AU';
      this.monthFormat = new Intl.DateTimeFormat(this.locale, {month:'long', timeZone:'UTC'});
      this.dateFormat = new Intl.DateTimeFormat(this.locale, {day:'numeric', month:'short', year:'numeric', timeZone:'UTC'});
      this.emptyYearText = this.querySelector('[data-empty-year]').textContent;
      this.events = [];
      this.imageCache = new Map();
      this.imageRequest = 0;
      const invalid = [];
      this.querySelectorAll('[data-event]').forEach(source => {
        const data = source.dataset;
        if (data.enabled !== 'true') return;
        const start = parseDate(data.start.trim());
        const end = data.end.trim() ? parseDate(data.end.trim()) : start;
        if (!start || !end || end < start || !data.title.trim() || key(end) < '2027-01-01' || key(start) > '2028-12-31') {
          invalid.push(data.title || 'Event');
          return;
        }
        const category = ['exhibition','conference','workshop','community','awareness','other','custom'].includes(data.category) ? data.category : 'other';
        const categoryLabel = category==='custom' ? (data.customLabel || '').trim() || this.labels.custom : this.labels[category];
        const filterKey = category==='custom' ? 'custom:'+categoryLabel.toLocaleLowerCase(this.locale) : category;
        this.events.push({...data, category, categoryLabel, filterKey, colour:customColour(data.customColour), start, end});
      });
      this.events.sort((a, b) => a.start - b.start || a.title.localeCompare(b.title));
      this.customFilters = new Map();
      this.events.filter(event => event.category==='custom').forEach(event => {
        if (this.customFilters.has(event.filterKey)) return;
        this.customFilters.set(event.filterKey,event);
        const button = create('button','');
        button.type = 'button';button.dataset.category = event.filterKey;
        button.setAttribute('aria-pressed','false');
        const dot = create('i','ipcal__dot ipcal--custom');dot.setAttribute('aria-hidden','true');
        this.applyStyle(dot,event);
        button.append(dot,document.createTextNode(event.categoryLabel));
        this.querySelector('.ipcal__filters').append(button);
      });
      this.querySelector('[data-sources]').hidden = true;
      this.querySelector('.ipcal__app').hidden = false;
      const editorMessage = this.querySelector('[data-editor-message]');
      if (editorMessage && invalid.length) editorMessage.textContent += ' ' + this.labels.dates_error + ' ' + invalid.join(', ');
      this.addEventListener('click', event => this.handleClick(event));
      this.querySelector('[data-month-select]').addEventListener('change', event => {
        this.month = Number(event.target.value);
        this.render();
      });
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('click', event => {if (event.target === this.dialog) this.dialog.close();});
      this.dialog.addEventListener('close', () => {this.imageRequest++;});
      this.blockHandler = event => {
        const selected = this.events.find(item => item.blockId === event.detail.blockId);
        if (!selected) return;
        const first = selected.start < new Date(Date.UTC(2027, 0, 1)) ? new Date(Date.UTC(2027,0,1)) : selected.start;
        this.year = first.getUTCFullYear();
        this.month = first.getUTCMonth();
        this.category = 'all';
        this.view = 'month';
        this.render();
      };
      document.addEventListener('shopify:block:select', this.blockHandler);
      this.render();
    }
    disconnectedCallback() {document.removeEventListener('shopify:block:select', this.blockHandler);}
    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-year')) this.year = Number(button.dataset.year);
      else if (button.hasAttribute('data-view')) this.view = button.dataset.view;
      else if (button.hasAttribute('data-category')) this.category = button.dataset.category;
      else if (button.hasAttribute('data-open-month')) {this.month = Number(button.dataset.openMonth);this.view = 'month';}
      else if (button.hasAttribute('data-prev') || button.hasAttribute('data-next')) {
        const next = new Date(Date.UTC(this.year, this.month + (button.hasAttribute('data-next') ? 1 : -1), 1));
        if (next.getUTCFullYear() < 2027 || next.getUTCFullYear() > 2028) return;
        this.year = next.getUTCFullYear();this.month = next.getUTCMonth();
      }
      else if (button.hasAttribute('data-event-index')) {this.openEvent(this.events[Number(button.dataset.eventIndex)]);return;}
      else if (button.hasAttribute('data-close')) {this.dialog.close();return;}
      else return;
      this.render();
      if (button.hasAttribute('data-open-month')) this.querySelector('[data-prev]').focus();
    }
    monthName(month) {return this.monthFormat.format(new Date(Date.UTC(this.year,month,1)));}
    monthEvents(month) {
      const start = new Date(Date.UTC(this.year,month,1));
      const end = new Date(Date.UTC(this.year,month+1,0));
      return this.events.filter(event => (this.category === 'all' || event.filterKey === this.category) && event.start <= end && event.end >= start);
    }
    dateRange(event) {return this.dateFormat.format(event.start) + (event.end > event.start ? ' – ' + this.dateFormat.format(event.end) : '');}
    applyStyle(node,event) {
      ['--event-accent','--event-bg','--event-ink'].forEach(name => node.style.removeProperty(name));
      if (event.category !== 'custom') return;
      node.style.setProperty('--event-accent',event.colour);
      node.style.setProperty('--event-bg',event.colour);
      node.style.setProperty('--event-ink',colourInk(event.colour));
    }
    eventButton(event, compact = false) {
      const button = create('button', (compact ? 'ipcal__day-event' : 'ipcal__event-card') + ' ipcal--' + event.category);
      button.type = 'button';
      this.applyStyle(button,event);
      button.dataset.eventIndex = this.events.indexOf(event);
      button.setAttribute('aria-label', event.title + ', ' + this.dateRange(event) + ', ' + event.categoryLabel);
      if (compact) button.textContent = event.title;
      else {
        button.append(create('strong', '', event.title));
        button.append(create('span', '', this.dateRange(event)));
        button.append(create('span', '', [event.categoryLabel, event.location].filter(Boolean).join(' · ')));
      }
      return button;
    }
    miniCalendar(month, events) {
      const mini = create('div','ipcal__mini');
      mini.setAttribute('aria-hidden','true');
      this.labels.weekdays.split(',').forEach(day => mini.append(create('span','ipcal__mini-label',day.slice(0,1))));
      const offset = (new Date(Date.UTC(this.year,month,1)).getUTCDay()+6)%7;
      for (let n=0;n<offset;n++) mini.append(create('span',''));
      const count = new Date(Date.UTC(this.year,month+1,0)).getUTCDate();
      for (let day=1;day<=count;day++) {
        const date = new Date(Date.UTC(this.year,month,day));
        const has = events.some(event => event.start<=date && event.end>=date);
        mini.append(create('span','ipcal__mini-day'+(has?' has-event':''),day));
      }
      return mini;
    }
    renderYear() {
      const grid = this.querySelector('[data-year-grid]');
      const fragment = document.createDocumentFragment();
      for(let month=0;month<12;month++) {
        const events = this.monthEvents(month);
        const card = create('section','ipcal__month-card');
        const heading = create('div','ipcal__card-top');
        heading.append(create('h2','',this.monthName(month)),create('span','ipcal__month-number',String(month+1).padStart(2,'0')));
        card.append(heading,this.miniCalendar(month,events));
        const contents = create('div','ipcal__month-events');
        if (!events.length) contents.append(create('p','ipcal__empty-month',this.labels.empty));
        events.forEach(event => contents.append(this.eventButton(event)));
        card.append(contents);
        const open = create('button','ipcal__month-open');
        open.type = 'button';open.dataset.openMonth = month;
        open.setAttribute('aria-label',this.labels.view_month + ' ' + this.monthName(month) + ' ' + this.year);
        open.append(create('span','',this.labels.view_month),create('span','','→'));
        card.append(open);fragment.append(card);
      }
      grid.replaceChildren(fragment);
    }
    renderMonth() {
      this.querySelector('[data-month-title]').textContent = this.monthName(this.month)+' '+this.year;
      this.querySelector('[data-prev]').disabled = this.year===2027 && this.month===0;
      this.querySelector('[data-next]').disabled = this.year===2028 && this.month===11;
      const select = this.querySelector('[data-month-select]');
      select.replaceChildren(...Array.from({length:12},(_,month) => {
        const option = create('option','',this.monthName(month));option.value = month;option.selected = month===this.month;return option;
      }));
      const events = this.monthEvents(this.month);
      const days = this.querySelector('[data-days]');
      const fragment = document.createDocumentFragment();
      this.labels.weekdays.split(',').forEach(day => fragment.append(create('div','ipcal__weekday',day)));
      const offset = (new Date(Date.UTC(this.year,this.month,1)).getUTCDay()+6)%7;
      for(let n=0;n<offset;n++) fragment.append(create('div','ipcal__day is-blank'));
      const count = new Date(Date.UTC(this.year,this.month+1,0)).getUTCDate();
      for(let day=1;day<=count;day++) {
        const date = new Date(Date.UTC(this.year,this.month,day));
        const cell = create('div','ipcal__day');
        cell.append(create('time','ipcal__date',day));
        cell.firstChild.dateTime = key(date);
        events.filter(event => event.start<=date && event.end>=date).forEach(event => cell.append(this.eventButton(event,true)));
        fragment.append(cell);
      }
      for(let n=0;n<(7-(offset+count)%7)%7;n++) fragment.append(create('div','ipcal__day is-blank'));
      days.replaceChildren(fragment);
      const mobile = this.querySelector('[data-mobile-month]');
      mobile.replaceChildren(...(events.length ? events.map(event=>this.eventButton(event)) : [create('p','ipcal__empty-month',this.labels.empty)]));
    }
    render() {
      this.querySelectorAll('[data-year]').forEach(button => button.setAttribute('aria-pressed',String(Number(button.dataset.year)===this.year)));
      this.querySelectorAll('[data-view]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.view===this.view)));
      this.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.category===this.category)));
      this.querySelector('[data-year-grid]').hidden = this.view !== 'year';
      this.querySelector('[data-month-panel]').hidden = this.view !== 'month';
      const matching = this.events.filter(event => (this.category==='all' || event.filterKey===this.category) && key(event.end)>=this.year+'-01-01' && key(event.start)<=this.year+'-12-31');
      const shown = this.view==='year' ? matching : this.monthEvents(this.month);
      const filterLabel = this.customFilters.get(this.category)?.categoryLabel || this.labels[this.category];
      this.querySelector('[data-status]').textContent = (this.view==='year'?this.year:this.monthName(this.month)+' '+this.year)+' · '+shown.length+' '+(shown.length===1?this.labels.event:this.labels.events)+(this.category==='all'?'':' · '+filterLabel);
      const empty = this.querySelector('[data-empty-year]');
      empty.hidden = matching.length>0 || this.view!=='year';
      if (this.category!=='all') empty.textContent = this.labels.no_match;
      else empty.textContent = this.emptyYearText || (this.emptyYearText = empty.textContent);
      if (this.view==='year') this.renderYear();else this.renderMonth();
    }
    openEvent(event) {
      this.dialog.className = 'ipcal__dialog ipcal--'+event.category;
      this.applyStyle(this.dialog,event);
      this.querySelector('[data-dialog-category]').textContent = event.categoryLabel;
      this.querySelector('[data-dialog-title]').textContent = event.title;
      this.querySelector('[data-dialog-date]').textContent = this.dateRange(event);
      this.querySelector('[data-dialog-location]').textContent = [event.venue,event.location].filter(Boolean).join(' · ');
      this.querySelector('[data-dialog-summary]').textContent = event.summary;
      this.setLink(this.querySelector('[data-dialog-link]'),event.url);
      this.setLink(this.querySelector('[data-dialog-map]'),event.mapUrl);
      this.dialog.showModal();
      this.loadImage(event);
    }
    setLink(link,value) {
      const url = safeUrl(value);
      link.hidden = !url;
      if (url) link.href = url.href;else link.removeAttribute('href');
    }
    async websiteImage(value) {
      const url = safeUrl(value);
      if (!url) return null;
      if (/\.(png|jpe?g|webp|avif|gif|svg)$/i.test(url.pathname)) return url.href;
      if (this.imageCache.has(url.href)) return this.imageCache.get(url.href);
      const request = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(),6000);
        try {
          const response = await fetch(url.href,{mode:'cors',credentials:'omit',referrerPolicy:'no-referrer',signal:controller.signal});
          if (!response.ok) return null;
          const type = response.headers.get('content-type') || '';
          if (type.startsWith('image/')) return safeUrl(response.url)?.href || null;
          if (type && !/text\/html|application\/xhtml/i.test(type)) return null;
          const content = await response.text();
          if (content.length>2000000) return null;
          const doc = new DOMParser().parseFromString(content,'text/html');
          const selectors = ['meta[property="og:image:secure_url"]','meta[property="og:image"]','meta[name="og:image"]','meta[name="twitter:image"]','meta[property="twitter:image"]','link[rel="image_src"]'];
          for (const selector of selectors) {
            const meta = doc.querySelector(selector);
            const image = safeUrl(meta?.getAttribute('content') || meta?.getAttribute('href'),response.url || url.href);
            if (image) return image.href;
          }
          return null;
        } catch {return null;} finally {clearTimeout(timeout);}
      })();
      this.imageCache.set(url.href,request);
      return request;
    }
    async loadImage(event) {
      const request = ++this.imageRequest;
      const wrap = this.querySelector('[data-image-wrap]');
      const status = this.querySelector('[data-image-status]');
      const help = this.querySelector('[data-image-help]');
      wrap.replaceChildren();wrap.hidden=true;status.hidden=true;if(help) help.hidden=true;
      const current = () => request===this.imageRequest && this.dialog.open;
      const show = url => new Promise(resolve => {
        const img = create('img','ipcal__dialog-image');
        img.alt = event.imageAlt || event.title;
        img.decoding = 'async';img.referrerPolicy = 'no-referrer';
        let timer;
        const complete = success => {clearTimeout(timer);img.onload=null;img.onerror=null;resolve(success);};
        img.onload = () => {
          if (!current()) {complete(false);return;}
          wrap.replaceChildren(img);wrap.hidden=false;complete(true);
        };
        img.onerror = () => complete(false);
        timer = setTimeout(() => complete(false),6000);
        img.src = url;
      });
      const supplied = safeUrl(event.imageUrl);
      const automatic = event.autoImage==='true' && safeUrl(event.url);
      if (!supplied && !automatic) return;
      status.hidden=false;
      let loaded = supplied ? await show(supplied.href) : false;
      if (current() && !loaded && automatic) {
        const image = await this.websiteImage(event.url);
        if (current() && image) loaded = await show(image);
      }
      if (!current()) return;
      status.hidden=true;
      if (help) help.hidden=loaded;
    }
  }
  customElements.define('ip-events-calendar',IPEventsCalendar);
})();
